<script lang="ts">
  import type { StorePinData } from './storeTypes';
  import type { Floor } from '../mapUtils';
  import { createEventDispatcher } from 'svelte';

  export let allStores: Map<number, StorePinData[]>;
  export let floors: Floor[] = [];
  export let currentFloorId: number | null;
  export let highlightedStoreId: string | null;
  export let isVisible: boolean;

  const dispatch = createEventDispatcher();

  let searchTerm = '';
  let touchStartY = 0;
  let isScrolling = false;

  function getFloorNameById(floorId: number): string {
    const floor = floors.find(f => f.id === floorId);
    if (!floor) return `Floor ${floorId}`;
    if (typeof floor.name === 'string') return floor.name;
    if (Array.isArray(floor.name)) {
      const localizedName = floor.name[0];
      return localizedName?.text || `Floor ${floor.id}`;
    }
    return `Floor ${floor.id}`;
  }

  $: filteredStores = () => {
    const stores: (StorePinData & { floorId: number })[] = [];
    const seenKeys = new Set<string>();
    if (!allStores) return stores;

    allStores.forEach((floorPins, floorId) => {
      floorPins.forEach(pin => {
        if (pin.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          // Create a unique key to detect duplicates
          const uniqueKey = `${pin.id}-${floorId}`;
          
          // Only add if we haven't seen this exact store on this floor before
          if (!seenKeys.has(uniqueKey)) {
            seenKeys.add(uniqueKey);
            stores.push({ ...pin, floorId });
          } else {
            console.warn(`Duplicate store detected: ID ${pin.id} on floor ${floorId} (${pin.name})`);
          }
        }
      });
    });

    // Sort by name
    return stores.sort((a, b) => a.name.localeCompare(b.name));
  };

  function selectStore(store: StorePinData & { floorId: number }) {
    dispatch('storeselect', { store });
  }

  function toggleSidebar() {
    dispatch('toggle');
  }

  function handleTouchStart(event: TouchEvent) {
    touchStartY = event.touches[0].clientY;
    isScrolling = false;
  }

  function handleTouchMove(event: TouchEvent) {
    const moveY = Math.abs(event.touches[0].clientY - touchStartY);
    if (moveY > 5) { // Threshold of 5px to detect a scroll
      isScrolling = true;
    }
  }

  function handleTouchEnd(store: StorePinData & { floorId: number }) {
    if (!isScrolling) {
      selectStore(store);
    }
    // Reset for the next touch
    isScrolling = false;
  }
</script>

<div class="store-sidebar" class:visible={isVisible}>
  <button class="toggle-btn" on:click={toggleSidebar}>
    {isVisible ? '‹' : '›'}
  </button>
  <div class="sidebar-content">
    <h2>Stores</h2>
    <input type="text" bind:value={searchTerm} placeholder="Search stores..." />
    <ul class="store-list">
      {#each filteredStores() as store, index (`${store.id}-${store.floorId}-${index}`)}
        <li
          class:highlighted={store.id === highlightedStoreId}
          on:click={() => selectStore(store)}
          on:touchstart={handleTouchStart}
          on:touchmove={handleTouchMove}
          on:touchend={() => handleTouchEnd(store)}
        >
          <span class="store-name">{store.name}</span>
          <span class="store-floor">{getFloorNameById(store.floorId)}</span>
        </li>
      {/each}
    </ul>
  </div>
</div>

<style>
  .store-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    width: 300px;
    height: 100%;
    background: #fff;
    box-shadow: 2px 0 5px rgba(0,0,0,0.1);
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    z-index: 200;
    display: flex;
    flex-direction: column;
    user-select: auto;
    pointer-events: auto;
  }

  .store-sidebar.visible {
    transform: translateX(0);
  }

  .toggle-btn {
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);
    width: 25px;
    height: 50px;
    background: #fff;
    border: 1px solid #ddd;
    border-left: none;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    cursor: pointer;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 2px 0 5px rgba(0,0,0,0.1);
    z-index: 201; /* Above sidebar */
    -webkit-appearance: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    color: var(--body-color);
  }

  .sidebar-content {
    padding: 15px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    height: 100%;
    align-items: center;
  }

  h2 {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 1.2em;
  }

  input {
    width: 100%;
    padding: 8px;
    margin-bottom: 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
  }

  .store-list {
    list-style: none;
    padding: 0;
    margin: 0;
    overflow-y: auto;
    flex-grow: 1;
  }

  .store-list li {
    padding: 10px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    -webkit-appearance: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    color: var(--body-color);
  }

  .store-list li:hover {
    background-color: #f5f5f5;
  }

  .store-list li.highlighted {
    background-color: #e8f0fe;
    font-weight: bold;
  }

  .store-name {
    flex-grow: 1;
  }

  .store-floor {
    font-size: 0.9em;
    color: #777;
    margin-left: 10px;
    white-space: nowrap;
  }
</style> 